/**
 * Post-processing for vision extraction — REQUIREMENTS.md §6.1.
 *
 * The model is asked for the merchant; these rules are the safety net for when
 * it returns something else. Every rule here corresponds to a real failure in
 * `reference/example-packet.xlsx`, the legacy system's raw output:
 *
 *   "Date paid May 29, 2026"          payment-confirmation text as vendor
 *   "disclosure. If you are not the"  email disclaimer boilerplate as vendor
 *   "Matthew Callison"                cardholder name from a rental agreement
 *   "Unknown Vendor"                  silently accepted when it read nothing
 *   46173.734178240746                a timestamp in a date-only field
 *
 * The legacy system's actual defect was not that it misread things — it is
 * that it accepted the misreads silently, so Matt corrected them by hand at
 * packet time. Everything rejected here is flagged for review rather than
 * discarded: a wrong value that announces itself costs seconds, a wrong value
 * that doesn't costs a reconciliation cycle.
 */

/** Confidence at or above this is treated as high (§7.1 step 6). */
export const HIGH_CONFIDENCE = 0.85

export type ExtractionRejectionReason =
  | 'email_boilerplate'
  | 'payment_confirmation_text'
  | 'cardholder_name'
  | 'unknown_placeholder'
  | 'implausible_vendor'
  | 'low_confidence'

export interface ExtractionRejection {
  field: 'vendor' | 'amount' | 'date' | 'overall'
  reason: ExtractionRejectionReason
  /** What the model returned, kept for the review UI and for auditing. */
  rejected: string
}

/** What the model returns, before any checking. */
export interface RawExtraction {
  vendor: string | null
  amount: number | null
  /** ISO 8601 UTC, or null. */
  date: string | null
  confidence: number
}

export interface CheckedExtraction {
  vendor: string | null
  amount: number | null
  date: string | null
  confidence: number
  /** True when anything was rejected or confidence is low (§6.1). */
  needsReview: boolean
  rejections: ExtractionRejection[]
}

/**
 * Phrases that appear in email footers and disclaimers, never in a merchant
 * name. Matched as substrings because the legacy failure was a *fragment* of a
 * disclaimer ("disclosure. If you are not the"), not a whole one.
 */
const EMAIL_BOILERPLATE = [
  'if you are not the',
  'intended recipient',
  'confidential',
  'disclosure',
  'unsubscribe',
  'do not reply',
  'this email',
  'this message',
  'privacy policy',
  'terms of service',
  'all rights reserved',
  'view in browser',
]

/** Leading words that mean the model grabbed a label, not a merchant. */
const LABEL_PREFIXES = [
  'date',
  'paid',
  'order',
  'confirmation',
  'receipt',
  'invoice',
  'transaction',
  'total',
  'subtotal',
  'payment',
  'ref',
  'reference',
]

/** Placeholders the legacy system wrote when it read nothing. */
const UNKNOWN_PLACEHOLDERS = [
  'unknown vendor',
  'unknown',
  'n/a',
  'na',
  'none',
  'not found',
  'unclear',
  'illegible',
  'unreadable',
]

/** A formatted date anywhere in the string, e.g. "May 29, 2026" or "5/29/26". */
const CONTAINS_DATE =
  /\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.?\s+\d{1,2}\b|\b\d{1,2}[/-]\d{1,2}[/-]\d{2,4}\b|\b\d{4}-\d{2}-\d{2}\b/i

export interface CheckOptions {
  /**
   * The cardholder's own name. The legacy system pulled it off a car-rental
   * agreement, where it appears more prominently than the rental company's.
   */
  cardholderName?: string | undefined
}

/**
 * Apply every §6.1 rule to one extraction.
 *
 * A rejected field becomes null and the receipt is flagged for review; the
 * rejected text is preserved so the review screen can show what was read and
 * why it was refused.
 */
export function checkExtraction(
  raw: RawExtraction,
  options: CheckOptions = {},
): CheckedExtraction {
  const rejections: ExtractionRejection[] = []
  let vendor = raw.vendor?.trim() ?? null

  if (vendor !== null && vendor !== '') {
    const reason = rejectVendorReason(vendor, options.cardholderName)
    if (reason !== null) {
      rejections.push({ field: 'vendor', reason, rejected: vendor })
      vendor = null
    }
  } else {
    vendor = null
  }

  // Low confidence flags the whole extraction for review but keeps the values:
  // a partly-right reading Matt can correct beats an empty form (§7.1 step 6).
  if (raw.confidence < HIGH_CONFIDENCE) {
    rejections.push({
      field: 'overall',
      reason: 'low_confidence',
      rejected: raw.confidence.toFixed(2),
    })
  }

  return {
    vendor,
    amount: raw.amount,
    date: raw.date,
    confidence: raw.confidence,
    needsReview: rejections.length > 0 || vendor === null || raw.amount === null,
    rejections,
  }
}

/** Why a vendor string should be refused, or null if it looks like a merchant. */
export function rejectVendorReason(
  vendor: string,
  cardholderName?: string | undefined,
): ExtractionRejectionReason | null {
  const value = vendor.trim()
  const lower = value.toLowerCase()

  if (UNKNOWN_PLACEHOLDERS.includes(lower)) return 'unknown_placeholder'

  if (EMAIL_BOILERPLATE.some((phrase) => lower.includes(phrase))) {
    return 'email_boilerplate'
  }

  const firstWord = lower.split(/[\s.:,]+/)[0] ?? ''
  if (LABEL_PREFIXES.includes(firstWord)) return 'payment_confirmation_text'
  if (CONTAINS_DATE.test(value)) return 'payment_confirmation_text'

  if (
    cardholderName !== undefined &&
    cardholderName.trim() !== '' &&
    namesMatch(value, cardholderName)
  ) {
    return 'cardholder_name'
  }

  // A merchant name has at least two characters and at least one letter.
  // "$", "—", and "12" are not merchants.
  if (value.length < 2 || !/[a-z]/i.test(value)) return 'implausible_vendor'

  // Sentence-length strings are captured prose, not a merchant name.
  if (value.split(/\s+/).length > 8) return 'implausible_vendor'

  return null
}

/** Case- and punctuation-insensitive name comparison, either order. */
function namesMatch(candidate: string, name: string): boolean {
  const normalize = (s: string) =>
    s
      .toLowerCase()
      .replace(/[^a-z\s]/g, '')
      .split(/\s+/)
      .filter(Boolean)
      .sort()
      .join(' ')
  return normalize(candidate) === normalize(name)
}

/**
 * Normalize an extracted date to midnight UTC on its calendar day.
 *
 * §6.1: row 9 of the reference file carries `46173.734178240746` — a full
 * timestamp where a date belongs. Times on a receipt date are noise at best
 * and, near midnight, put the purchase on the wrong day.
 */
export function normalizeExtractedDate(iso: string | null): string | null {
  if (iso === null || iso === '') return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  return `${date.toISOString().slice(0, 10)}T00:00:00.000Z`
}

/**
 * Whether a date is plausible for a receipt being captured now.
 *
 * Guards against a misread year putting a purchase decades away. Receipts are
 * captured close to purchase, and a fiscal year is bounded — anything outside
 * roughly a year either side is a misread, not a late filing.
 */
export function isPlausibleReceiptDate(iso: string | null, now = new Date()): boolean {
  if (iso === null) return false
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return false
  const days = (date.getTime() - now.getTime()) / 86_400_000
  return days > -400 && days < 30
}

/**
 * A one-line explanation of why a field was refused, addressed to the user.
 *
 * The review screen shows this next to the empty field. "Vendor is blank" is
 * not actionable; "the receipt's payment-confirmation line was read instead of
 * the merchant" tells Matt what to look for on the image in front of him.
 */
export function describeRejection(rejection: ExtractionRejection): string {
  switch (rejection.reason) {
    case 'email_boilerplate':
      return 'Read email footer text instead of the merchant.'
    case 'payment_confirmation_text':
      return 'Read a payment or confirmation line instead of the merchant.'
    case 'cardholder_name':
      return 'Read your own name — common on rental and hotel paperwork.'
    case 'unknown_placeholder':
      return "Couldn't find a merchant name on the receipt."
    case 'implausible_vendor':
      return "What was read doesn't look like a merchant name."
    case 'low_confidence':
      return 'The reading was uncertain — check every field.'
  }
}

/**
 * How much work a receipt still needs — §7.1 step 6.
 *
 * `confirm` is the fast path: a clean, high-confidence read where the only
 * thing missing is which budget code it belongs to. `correct` means the form
 * opens pre-filled but flagged. `manual` means extraction produced nothing
 * usable, so the form opens empty rather than pretending otherwise.
 */
export type ReviewReadiness = 'confirm' | 'correct' | 'manual'

export function reviewReadiness(input: {
  status: 'pending' | 'done' | 'failed' | null
  needsReview: boolean
  vendor: string | null
  amount: number | null
  date: string | null
}): ReviewReadiness {
  if (input.status !== 'done') return 'manual'
  if (input.vendor === null && input.amount === null) return 'manual'
  if (input.needsReview) return 'correct'
  if (input.vendor === null || input.amount === null || input.date === null) {
    return 'correct'
  }
  return 'confirm'
}

/**
 * Detect a probable re-capture of the same receipt — §6.1, last bullet.
 *
 * The legacy system produced multiple documents from one receipt; the
 * reference packet shows a $150.36 tolls charge twice. Same vendor, same
 * amount, same capture channel, inside a day is almost always one receipt
 * captured twice, not two identical purchases.
 */
export interface DuplicateCandidate {
  vendor: string | null
  amount: number | null
  captureSource: string
  capturedAt: string
}

export function findLikelyDuplicate<T extends DuplicateCandidate>(
  incoming: DuplicateCandidate,
  existing: readonly T[],
  windowHours = 24,
): T | null {
  // Pulled out of the closure: narrowing on `incoming` doesn't survive into the
  // callback, and the alternative is a non-null assertion that would silently
  // stop protecting anything if these guards were ever reordered.
  const { vendor, amount, captureSource } = incoming
  if (vendor === null || amount === null) return null

  const targetVendor = vendor.trim().toLowerCase()
  const targetCents = Math.round(amount * 100)
  const incomingAt = new Date(incoming.capturedAt).getTime()
  const windowMs = windowHours * 3_600_000

  return (
    existing.find((candidate) => {
      if (candidate.vendor === null || candidate.amount === null) return false
      if (candidate.captureSource !== captureSource) return false
      if (Math.round(candidate.amount * 100) !== targetCents) return false
      if (candidate.vendor.trim().toLowerCase() !== targetVendor) return false
      const delta = Math.abs(new Date(candidate.capturedAt).getTime() - incomingAt)
      return delta <= windowMs
    }) ?? null
  )
}

/**
 * The *other* photo of a restaurant bill — §8.1's record-keeping requirement
 * meeting §7.4's arithmetic.
 *
 * A restaurant hands over two pieces of paper. The itemised slip shows what
 * was ordered, which the auditors need to see; the signed card slip shows the
 * total including the tip, which is the figure that reaches the P-Card
 * statement and the only one that may come off a budget. Both have to be
 * filed, and exactly one of them may be counted.
 *
 * `findLikelyDuplicate` above cannot help here: it demands the amounts match
 * to the cent, and these two differ *by definition*. So the app said nothing,
 * and filing both photos the obvious way produced two charges — which is how
 * a $48.40 dinner came off Matt's budget as $88.80 on 2026-10-14. The
 * workaround he found on his own was deleting the itemised photo, which keeps
 * the arithmetic honest by destroying the evidence the audit wants.
 *
 * Matched on: same vendor, within a day, and a *higher* amount that exceeds
 * the lower one by no more than `maxUpliftFraction`. The direction matters —
 * a tip is added, never subtracted — and the ceiling keeps two genuinely
 * separate meals at one vendor from being offered as one purchase. 60% is
 * deliberately generous: a 20% tip on a split bill, a rounded-up total, and a
 * service charge can all stack, and the prompt only ever *offers*.
 */
export interface SamePurchaseCandidate {
  vendor: string | null
  amount: number | null
  /** Purchase date for a charge, capture date for a receipt. */
  at: string
}

export function findSamePurchase<T extends SamePurchaseCandidate>(
  incoming: SamePurchaseCandidate,
  existing: readonly T[],
  { windowHours = 24, maxUpliftFraction = 0.6 } = {},
): T | null {
  const { vendor, amount } = incoming
  if (vendor === null || amount === null || amount === 0) return null

  const targetVendor = vendor.trim().toLowerCase()
  const incomingAt = new Date(incoming.at).getTime()
  if (Number.isNaN(incomingAt)) return null
  const windowMs = windowHours * 3_600_000

  return (
    existing.find((candidate) => {
      if (candidate.vendor === null || candidate.amount === null) return false
      if (candidate.vendor.trim().toLowerCase() !== targetVendor) return false

      const candidateAt = new Date(candidate.at).getTime()
      if (Number.isNaN(candidateAt)) return false
      if (Math.abs(candidateAt - incomingAt) > windowMs) return false

      const lower = Math.min(candidate.amount, amount)
      const higher = Math.max(candidate.amount, amount)
      // An exact match is the other function's business — this one is about
      // two amounts that differ by a tip.
      if (lower <= 0 || higher === lower) return false
      return (higher - lower) / lower <= maxUpliftFraction
    }) ?? null
  )
}

/**
 * Which of two amounts for one purchase is the one that counts.
 *
 * Always the larger: the tip is added after the itemised slip is printed, and
 * the card is charged the total. That is what appears on the statement, and a
 * charge that disagrees with the statement is one the reconciliation cannot
 * match.
 */
export function chargeableAmount(a: number, b: number): number {
  return Math.max(a, b)
}
